import React from 'react'

import { Lock, type LucideIcon, Zap } from 'lucide-react'
import Image from 'next/image'

import { HighlightedText } from '~/components/HighlightedText'
import {
  type CLLayout,
  type CLSize,
  NetworkLogo,
  Tooltip,
} from '~/components/component-library'
import {
  TokenDisplayProps,
  TokenDisplayViewProps,
} from '~/components/component-library/TokenDisplay/types'
import { getChain, SupportedChainId } from '~/constants/chains'
import { useActiveOrgSmartAccountsChainIds } from '~/hooks/org'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useVaultApy } from '~/hooks/useTokens'
import { IAddress } from '~/types'
import { useChainLabel } from '~/utils/chainLabels'
import { cn } from '~/utils/tailwind-utils'
import { getVaultAssetSymbol, isVaultToken } from '~/utils/vault'

export type {
  TokenDisplayProps,
  TokenDisplayViewProps,
} from '~/components/component-library/TokenDisplay/types'

const SIZE_CONFIG = {
  small: {
    text: 'text-sm',
    chainLabel: 'text-xs leading-4 h-3',
    indicatorIcon: 'h-3 w-3',
    symbolRow: 'h-5 leading-5',
    logo: {
      standard: 'h-5 w-5',
      twoLine: 'h-8 w-8',
    },
  },
  base: {
    text: 'text-base',
    chainLabel: 'text-sm leading-4 h-4',
    indicatorIcon: 'h-3.5 w-3.5',
    symbolRow: 'h-6 leading-6',
    logo: {
      standard: 'h-6 w-6',
      twoLine: 'h-9 w-9',
    },
  },
} as const

export const InlinePositionIcon = ({ className }: { className?: string }) => (
  <Zap
    className={cn(
      'mb-0.5 ml-0.5 inline-block h-[0.875em] w-[0.875em] align-middle',
      className,
    )}
  />
)

const IndicatorIcon = ({
  icon: Icon,
  tooltip,
  size,
  className,
}: {
  icon: LucideIcon
  tooltip?: string
  size: CLSize
  className?: string
}) => {
  const node = (
    <Icon
      className={cn(
        'text-secondary',
        SIZE_CONFIG[size].indicatorIcon,
        className,
      )}
    />
  )
  if (!tooltip) return node
  return (
    <Tooltip content={tooltip} disableHoverableContent>
      {node}
    </Tooltip>
  )
}

/**
 * Pure presentational component for TokenDisplay.
 * Use this directly in optimized contexts where you want to lift hook calls to a parent.
 */
export const TokenDisplayView: React.FC<TokenDisplayViewProps> = (props) => {
  const {
    address,
    chainId,
    layout,
    hideInactiveChainIndicator = false,
    hideNetworkLogo = false,
    symbol,
    logoUri,
    isPosition,
    isInactiveChain,
    chainLabel,
    highlightText,
  } = props
  const size =
    layout === 'inline-text'
      ? 'base'
      : ('size' in props && props.size) || 'base'

  const sizeStyles = SIZE_CONFIG[size]

  const logo =
    layout === 'inline-text' ? null : (
      <TokenDisplayLogo
        tokenMetadata={{ logoUri: logoUri ?? '', symbol }}
        chainId={chainId}
        sizeStyles={sizeStyles}
        layout={layout}
        size={size}
        hideNetworkLogo={hideNetworkLogo}
      />
    )

  const symbolElement = (
    <span className={cn('min-w-0 truncate text-primary', sizeStyles.text)}>
      <HighlightedText text={symbol} highlight={highlightText} />
    </span>
  )

  const positionIndicator = isPosition && (
    <PositionIndicator chainId={chainId} vault={address} size={size} />
  )

  const lockIndicatorContent = getChain(chainId)
    ? `Turn on ${chainLabel} to interact with this token`
    : `${chainLabel} is not fully supported on Splits.`

  const lockIconIndicator = !hideInactiveChainIndicator && isInactiveChain && (
    <IndicatorIcon icon={Lock} tooltip={lockIndicatorContent} size={size} />
  )

  const symbolGroup = (
    <span className="flex min-w-0 items-center space-x-1">
      {symbolElement}
      {lockIconIndicator}
      {positionIndicator}
    </span>
  )

  switch (layout) {
    case 'standard':
      return (
        <span
          className={cn(
            'flex w-full min-w-0 items-center space-x-2',
            sizeStyles.text,
          )}
        >
          {logo}
          {symbolGroup}
        </span>
      )

    case 'two-line':
      return (
        <div
          className={cn('flex w-full items-center gap-2.5', sizeStyles.text)}
        >
          {logo}
          <div className="flex w-full min-w-0 flex-col items-start justify-center gap-0.5">
            <span
              className={cn(
                'flex w-full min-w-0 items-center space-x-2 truncate',
                sizeStyles.symbolRow,
              )}
            >
              {symbolGroup}
              {props.suffix}
            </span>
            <span
              className={cn(
                'flex flex-shrink-0 items-center gap-1.5 text-secondary',
                sizeStyles.chainLabel,
              )}
            >
              {props.protocol ? props.protocol + ' on ' : null}
              {chainLabel}
              {props.secondaryLabel}
            </span>
          </div>
        </div>
      )

    case 'inline-text':
      return (
        <span className="whitespace-nowrap">
          {symbol}
          {isPosition && <InlinePositionIcon />}
        </span>
      )
  }
}

/**
 * TokenDisplay component for consistently displaying token information across the app.
 *
 * @param address - Token contract address
 * @param chainId - Chain ID where the token exists
 * @param layout - 'standard', 'two-line', or 'inline-text'
 * @param size - 'small' or 'base' (available for standard and two-line layouts)
 */
const TokenDisplayInner: React.FC<TokenDisplayProps> = (props) => {
  const { address, chainId } = props
  const isPosition = isVaultToken(chainId, address)

  const tokenMetadata = useTokenMetadata({ address, chainId })

  const chainLabel = useChainLabel(chainId)
  const activeChains = useActiveOrgSmartAccountsChainIds()

  const isInactiveChain = !activeChains.includes(chainId)

  const symbol = React.useMemo(() => {
    // For positions, metadata is fetched for the underlying asset, so its
    // symbol usually matches; the registry lookup covers the metadata-loading
    // window so positions never flash UNKNOWN.
    if (isPosition) {
      const vaultAssetSymbol = getVaultAssetSymbol(chainId, address)
      if (vaultAssetSymbol) return vaultAssetSymbol
    }
    return tokenMetadata?.symbol || 'UNKNOWN'
  }, [isPosition, chainId, address, tokenMetadata?.symbol])

  return (
    <TokenDisplayView
      {...props}
      symbol={symbol}
      logoUri={tokenMetadata?.logoUri}
      isPosition={isPosition}
      isInactiveChain={isInactiveChain}
      chainLabel={chainLabel}
    />
  )
}

// The logo only renders in layouts that show a token mark — inline-text omits
// it entirely.
type TokenLogoLayout = Extract<CLLayout, 'standard' | 'two-line'>

const TokenDisplayLogo = ({
  tokenMetadata,
  sizeStyles,
  chainId,
  layout,
  size,
  hideNetworkLogo,
}: {
  tokenMetadata: { logoUri: string; symbol: string }
  chainId: SupportedChainId
  sizeStyles: (typeof SIZE_CONFIG)[CLSize]
  layout: TokenLogoLayout
  size: CLSize
  hideNetworkLogo: boolean
}) => {
  const [hasErrored, setHasErrored] = React.useState(false)
  const [hasLoaded, setHasLoaded] = React.useState(false)

  const logoSize =
    layout === 'two-line' ? sizeStyles.logo.twoLine : sizeStyles.logo.standard
  const showImage = tokenMetadata?.logoUri && !hasErrored

  return (
    <div className={cn('relative flex-shrink-0', logoSize)}>
      {!hideNetworkLogo && (
        <div className="absolute -bottom-0.5 -right-0.5 z-10 flex items-center justify-center">
          <NetworkLogo
            chainId={chainId}
            size={layout === 'standard' || size === 'small' ? 'xs' : 'sm'}
          />
        </div>
      )}
      {showImage && (
        <Image
          src={tokenMetadata.logoUri}
          alt={tokenMetadata.symbol}
          fill
          className="z-9 rounded-full object-cover"
          onLoad={() => setHasLoaded(true)}
          onError={() => setHasErrored(true)}
        />
      )}

      {(!hasLoaded || hasErrored || !showImage) && (
        <div className="absolute inset-0 rounded-full bg-gray-200 opacity-80 dark:bg-gray-700" />
      )}
    </div>
  )
}

const PositionIndicator = ({
  chainId,
  vault,
  size = 'base',
}: {
  chainId: SupportedChainId
  vault: IAddress
  size: CLSize
}) => {
  const { data: apy } = useVaultApy(chainId, vault)

  return (
    <IndicatorIcon
      icon={Zap}
      tooltip={apy ? `${apy.toFixed(1)}% APY` : 'Earn position'}
      size={size}
    />
  )
}

export const TokenDisplay = React.memo(TokenDisplayInner)
TokenDisplay.displayName = 'TokenDisplay'
